import {
  Component,
  Input,
  booleanAttribute,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';

import { SuiAccordionComponent } from './sui-accordion.component';

let nextId = 0;

/**
 * One panel of a `<sui-accordion>`. Project the header into the `[sui-accordion-header]` slot
 * (rich markup welcome — e.g. a count badge); the default-projected content is the collapsible
 * body. Trigger button + rotating chevron are built in; ARIA (`aria-expanded`/`aria-controls` +
 * `role=region`/`aria-labelledby`) is wired here, and collapsed content is `inert` (not focusable).
 */
@Component({
  selector: 'sui-accordion-item',
  standalone: true,
  imports: [FaIconComponent],
  template: `
    <div class="sui-accordion__item">
      <button
        type="button"
        class="sui-accordion__trigger"
        [id]="triggerId"
        [attr.aria-expanded]="accordion.isOpen(value)"
        [attr.aria-controls]="contentId"
        [disabled]="disabled || accordion.disabled"
        (click)="accordion.toggle(value)"
      >
        <span class="sui-accordion__title">
          <ng-content select="[sui-accordion-header]"></ng-content>
        </span>
        <fa-icon
          class="sui-accordion__chevron"
          [icon]="faChevron"
          aria-hidden="true"
        ></fa-icon>
      </button>
      <div
        role="region"
        class="sui-accordion__content"
        [class.is-open]="accordion.isOpen(value)"
        [id]="contentId"
        [attr.aria-labelledby]="triggerId"
        [attr.inert]="accordion.isOpen(value) ? null : ''"
      >
        <!-- Grid item: padding-free so the 0fr track can collapse it fully. -->
        <div class="sui-accordion__content-clip">
          <div class="sui-accordion__body">
            <ng-content></ng-content>
          </div>
        </div>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './sui-accordion.component.scss',
})
export class SuiAccordionItemComponent {
  /** The key that identifies this item in the accordion's `value`. */
  @Input({ required: true }) value!: string;
  @Input({ transform: booleanAttribute }) disabled = false;

  protected readonly accordion = inject(SuiAccordionComponent);
  protected readonly faChevron = faChevronDown;

  private readonly uid = ++nextId;
  protected readonly triggerId = `sui-accordion-trigger-${this.uid}`;
  protected readonly contentId = `sui-accordion-content-${this.uid}`;
}
